import { CantileverButton } from 'components/CantileverButton'
import { X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDeficiency,
  GeckoRiskAssessmentConfig,
  mapRiskClassToRiskScore,
} from 'types'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: #262626;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: auto;
  max-width: 30rem;
  max-height: 80vh;
  overflow-y: auto;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`

const ModalTitle = styled.h3`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`

interface DeficiencyRiskSelectionModalProps {
  isOpen: boolean
  deficiency: GeckoComponentDeficiency | null
  onClose: () => void
  onSave: (riskScore: number, likelihood: number, consequence: number) => void
  riskAssessmentConfig?: GeckoRiskAssessmentConfig
  RiskMatrixComponent: React.ComponentType<{
    selectedPosition: { row: number; col: number } | null
    onCellClick: (value: string, position: { row: number; col: number }) => void
  }>
}

export const DeficiencyRiskSelectionModal: React.FC<
  DeficiencyRiskSelectionModalProps
> = ({
  isOpen,
  deficiency,
  onClose,
  onSave,
  riskAssessmentConfig,
  RiskMatrixComponent,
}) => {
  const [tempRiskScore, setTempRiskScore] = useState(0)
  const [tempLikelihood, setTempLikelihood] = useState(0)
  const [tempConsequence, setTempConsequence] = useState(0)

  useEffect(() => {
    if (deficiency) {
      setTempRiskScore(deficiency.data.riskScore || 0)
      setTempLikelihood(deficiency.data.likelihood || 0)
      setTempConsequence(deficiency.data.consequence || 0)
    }
  }, [deficiency])

  const handleSave = () => {
    onSave(tempRiskScore, tempLikelihood, tempConsequence)
  }

  const handleRiskChange = (
    risk: string,
    likelihood: number,
    consequence: number
  ) => {
    const riskScore = mapRiskClassToRiskScore(risk, riskAssessmentConfig)
    setTempRiskScore(riskScore)
    setTempLikelihood(likelihood)
    setTempConsequence(consequence)
  }

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            Set Risk - {deficiency?.data.title || 'Deficiency'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        <div>
          <RiskMatrixComponent
            selectedPosition={
              tempLikelihood > 0 && tempConsequence > 0
                ? {
                    row: tempLikelihood - 1,
                    col: tempConsequence - 1,
                  }
                : null
            }
            onCellClick={(value, position) => {
              const likelihood = position.row + 1
              const consequence = position.col + 1
              handleRiskChange(value, likelihood, consequence)
            }}
          />
        </div>

        <ModalActions>
          <CantileverButton onClick={onClose}>Cancel</CantileverButton>
          <CantileverButton
            primary
            disabled={tempRiskScore === 0}
            onClick={handleSave}>
            Save
          </CantileverButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  )
}
